import { useEffect, useState } from "react";
import { ClipboardList, FileCheck2, Flame, Target } from "lucide-react";
import { getDashboardSummaryCards } from "utils/supabase";
import enarmApexMark from "assets/img/enarm-apex-logo.png";

const iconMap = {
  "clipboard-list": ClipboardList,
  "file-check": FileCheck2,
  target: Target,
  flame: Flame,
};

const iconColors = {
  indigo: { background: "#e0e7ff", color: "#4f46e5" },
  sky: { background: "#e0f2fe", color: "#0284c7" },
  pink: { background: "#fce7f3", color: "#db2777" },
  orange: { background: "#ffedd5", color: "#ea580c" },
};

function normalizeCards(rawData) {
  if (Array.isArray(rawData)) {
    if (rawData[0]?.cards) return Array.isArray(rawData[0].cards) ? rawData[0].cards : [];
    return rawData[0]?.title || rawData[0]?.key ? rawData : [];
  }
  return Array.isArray(rawData?.cards) ? rawData.cards : [];
}

function numberValue(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function isStreak(card) {
  const text = `${card?.title || ""} ${card?.key || ""} ${card?.icon || ""}`.toLowerCase();
  return text.includes("racha") || text.includes("streak") || text.includes("flame");
}

function isAverage(card) {
  const text = `${card?.title || ""} ${card?.key || ""}`.toLowerCase();
  return text.includes("promedio") || text.includes("average");
}

function getActivityType(card) {
  const text = `${card?.title || ""} ${card?.key || ""}`.toLowerCase();
  if (text.includes("pregunta")) return "preguntas";
  if (text.includes("examen") || text.includes("exámenes")) return "exámenes";
  return null;
}

function getMobileCardTitle(card) {
  const activityType = getActivityType(card);
  if (activityType === "preguntas") return "PREGUNTAS";
  if (activityType === "exámenes") return "EXÁMENES";
  return card.title;
}

function footerMessage(card, value) {
  const activityType = getActivityType(card);
  if (activityType) {
    const change = numberValue(card.change_percentage);
    if (change > 0) return `Vas avanzando con tus ${activityType}.`;
    if (change < 0) return `Sigue practicando tus ${activityType}.`;
    return `Mantén el ritmo con tus ${activityType}.`;
  }

  if (isStreak(card)) return value <= 0 ? "Comienza hoy tu racha." : "Sigue constante.";
  if (isAverage(card)) return value < 60 ? "Practica para mejorar." : "Buen rendimiento.";
  return card?.change_label || "";
}

export default function MobileHeaderStats({ showLogo = true, adminSpacing = false }) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    getDashboardSummaryCards()
      .then((data) => {
        if (mounted) setCards(normalizeCards(data));
      })
      .catch((error) => console.error(error))
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const visibleCards = loading ? [0, 1, 2, 3].map((key) => ({ key, loading: true })) : cards;

  return (
    <section style={{ ...styles.shell, ...(adminSpacing ? styles.adminShell : {}) }}>
      {showLogo ? <img alt="ENARM Apex" src={enarmApexMark} style={styles.logo} /> : null}
      <div style={styles.grid}>
        {visibleCards.map((card) => {
          if (card.loading) return <div key={card.key} style={styles.card} />;

          const streak = isStreak(card);
          const rawValue = numberValue(card.value);
          const value = streak ? Math.max(0, Math.floor(rawValue)) : Math.max(0, Math.min(100, rawValue));
          const Icon = iconMap[card.icon];
          const colors = iconColors[card.color] || iconColors.indigo;
          const title = streak ? `${value} ${value === 1 ? "día" : "días"}` : `${card.value}${card.value_suffix || ""}`;
          const footer = footerMessage(card, value);

          return (
            <div key={card.key} style={styles.card}>
              <div style={styles.cardTop}>
                <div style={styles.copy}>
                  <span style={styles.subtitle}>{getMobileCardTitle(card)}</span>
                  <strong style={styles.title}>{title}</strong>
                </div>
                <div style={{ ...styles.icon, backgroundColor: colors.background, color: colors.color }}>
                  {Icon ? <Icon size={16} /> : null}
                </div>
              </div>
              {footer ? <span style={styles.footer}>{footer}</span> : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}

const styles = {
  shell: {
    backgroundColor: "transparent",
    padding: "0px 12px 0px",
  },
  adminShell: {
    padding: "16px 12px 18px",
  },
  logo: {
    display: "block",
    height: 150,
    margin: "-22px auto -17px",
    objectFit: "contain",
    width: 150,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 8,
    maxWidth: 720,
    margin: "0 auto",
  },
  card: {
    minWidth: 0,
    minHeight: 82,
    backgroundColor: "#ffffff",
    border: "1px solid #d5e1f0",
    borderRadius: 14,
    boxShadow: "0 5px 12px rgba(15, 23, 42, 0.12)",
    padding: "10px 10px 9px",
  },
  cardTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 6,
  },
  copy: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 3,
  },
  subtitle: {
    overflow: "hidden",
    color: "#334e73",
    fontSize: 10,
    fontWeight: 600,
    lineHeight: 1.1,
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  title: {
    color: "#0f172a",
    fontSize: 20,
    lineHeight: 1,
  },
  icon: {
    alignItems: "center",
    borderRadius: 10,
    display: "flex",
    flexShrink: 0,
    height: 32,
    justifyContent: "center",
    width: 32,
  },
  footer: {
    color: "#64748b",
    display: "block",
    fontSize: 9,
    fontWeight: 600,
    lineHeight: 1.15,
    marginTop: 8,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
};
